import {
  type UnionAddress,
  getBlockchainFromAddress,
} from "@anaconda/core/blockchain/address"
import { getStaticConfigByBlockchain } from "@anaconda/core/blockchain/configs"
import {
  Blockchain,
  type Blockchain as BlockchainType,
} from "@anaconda/core/blockchain/types"

/** Pick wallet union address that matches the active blockchain filter. */
export function pickOwnerUnionAddress(
  blockchains: BlockchainType[] | undefined,
  options: {
    evmUnion?: UnionAddress
    solanaUnion?: UnionAddress
  },
): UnionAddress | undefined {
  const { evmUnion, solanaUnion } = options

  if (!blockchains?.length) {
    return undefined
  }

  const includesSolana = blockchains.includes(Blockchain.SOLANA)
  const includesEvm = blockchains.some((chain) => chain !== Blockchain.SOLANA)

  if (includesSolana && !includesEvm) {
    return solanaUnion
  }

  if (includesEvm && !includesSolana) {
    return evmUnion
  }

  // Mixed Solana + EVM filter: prefer connected EVM, else Solana.
  return evmUnion ?? solanaUnion
}

const isEvmBlockchain = (blockchain: BlockchainType) =>
  getStaticConfigByBlockchain(blockchain)?.isEvm === true

/**
 * Keep owner only when it can own items on a chain in the blockchain filter
 * (search query path). An EVM wallet has one `ETHEREUM:` union address for
 * every EVM chain, so it matches Robinhood, Base, etc. as well.
 */
export function ownerCompatibleWithBlockchains(
  owner: UnionAddress | undefined,
  blockchains: Blockchain[] | undefined,
): UnionAddress | undefined {
  if (!owner || !blockchains?.length) return undefined

  try {
    const ownerBlockchain = getBlockchainFromAddress(owner)
    const compatible = blockchains.some(
      (chain) =>
        chain === ownerBlockchain ||
        (ownerBlockchain === Blockchain.ETHEREUM && isEvmBlockchain(chain)),
    )
    return compatible ? owner : undefined
  } catch {
    return undefined
  }
}
